import { slateToHtml } from '@amelio/ui-library';
import { useCallback } from 'react';

import { cardCurrentMessage, cardCurrentTitle } from './communicationCardState';

import type { CommunicationCardKey, CommunicationCardState } from './communicationCardState';
import type { useAmelioTranslations } from '@/hooks';
import type { useError } from '@/hooks/utils/useError';

import { useSendCycleEvaluationGroupTestEmail, useSendCycleEvaluationTestEmail } from '@/api/email';
import { showSuccessToast } from '@/plugins/Toast';

interface UseCommunicationTestEmailParams {
    cards: Partial<Record<CommunicationCardKey, CommunicationCardState>>;
    cycleEvaluationGroupId: string | undefined;
    t: ReturnType<typeof useAmelioTranslations>['t'];
    handleError: ReturnType<typeof useError>['handleError'];
}

/**
 * Owns the "send a test email" action for a communication card, on either of the two contracts
 * a block can be reached through: a single evaluation's own test-email route (launch blocks) or
 * the group's route (every other block).
 */
export const useCommunicationTestEmail = ({
    cards,
    cycleEvaluationGroupId,
    t,
    handleError,
}: UseCommunicationTestEmailParams) => {
    const { mutateAsync: sendTestEmail, isPending: isSendingTestEmail } =
        useSendCycleEvaluationTestEmail();
    const { mutateAsync: sendGroupTestEmail, isPending: isSendingGroupTestEmail } =
        useSendCycleEvaluationGroupTestEmail();

    const reportTestEmailOutcome = useCallback(
        async (send: () => Promise<unknown>) => {
            try {
                await send();

                showSuccessToast({
                    title: t('evaluation360ScheduleCreation.testEmail.successTitle'),
                    message: t('evaluation360ScheduleCreation.testEmail.successMessage'),
                });
            } catch (error) {
                handleError({ error });
            }
        },
        [t, handleError],
    );

    const buildOnTestEmail = useCallback(
        (key: CommunicationCardKey, cycleEvaluationId?: string) => async () => {
            const card = cards[key];

            if (!card) {
                return;
            }

            if (!cycleEvaluationId) {
                return;
            }

            await reportTestEmailOutcome(() =>
                sendTestEmail({
                    emailTitle: cardCurrentTitle(card),
                    emailMessage: slateToHtml(cardCurrentMessage(card)),
                    cycleEvaluationId,
                    emailAddress: card.testEmailAddress,
                    selectedLang: card.language,
                }),
            );
        },
        [cards, sendTestEmail, reportTestEmailOutcome],
    );

    const buildOnGroupTestEmail = useCallback(
        (key: CommunicationCardKey) => async () => {
            const card = cards[key];

            if (!card || !cycleEvaluationGroupId) {
                return;
            }

            await reportTestEmailOutcome(() =>
                sendGroupTestEmail({
                    emailTitle: cardCurrentTitle(card),
                    emailMessage: slateToHtml(cardCurrentMessage(card)),
                    cycleEvaluationGroupId,
                    emailAddress: card.testEmailAddress,
                    selectedLang: card.language,
                }),
            );
        },
        [cards, sendGroupTestEmail, reportTestEmailOutcome, cycleEvaluationGroupId],
    );

    return {
        buildOnTestEmail,
        buildOnGroupTestEmail,
        isSendingTestEmail,
        isSendingGroupTestEmail,
    };
};
